import React, { FC, useState } from "react";
import { ChevronDown, ChevronRight } from "react-bootstrap-icons";
import "./styles.scss";

interface SmartJsonRendererProps {
  data: any;
  depth?: number;
  path?: string;
}

export const SmartJsonRenderer: FC<SmartJsonRendererProps> = ({
  data,
  depth = 0,
  path = "root",
}) => {
  // State to track collapsed nodes
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());
  // Convert camelCase or PascalCase to Title Case
  const toTitleCase = (str: string): string => {
    // Handle acronyms and special cases
    const acronyms = [
      "US",
      "ID",
      "SSN",
      "DOB",
      "ECOA",
      "OFAC",
      "MLA",
      "KYC",
      "API",
    ];

    // Split on capital letters, numbers, and underscores
    const words = str
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // Split consecutive caps before last cap
      .replace(/([a-z\d])([A-Z])/g, "$1 $2") // Split on camelCase
      .replace(/([a-zA-Z])(\d)/g, "$1 $2") // Split letters from numbers
      .replace(/_/g, " ") // Replace underscores with spaces
      .split(" ");

    return words
      .map((word) => {
        const upperWord = word.toUpperCase();
        // Keep acronyms as-is
        if (acronyms.includes(upperWord)) {
          return upperWord;
        }
        // Otherwise, capitalize first letter
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(" ")
      .trim();
  };

  // Check if value is a primitive
  const isPrimitive = (val: any): boolean => {
    return (
      val === null ||
      val === undefined ||
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean"
    );
  };

  // Check if an object has only one key and that key's value is also an object
  const getCollapsiblePath = (
    obj: any
  ): { path: string[]; finalValue: any } | null => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return null;
    }

    const keys = Object.keys(obj);
    if (keys.length !== 1) {
      return null;
    }

    const key = keys[0];
    const value = obj[key];

    // If the value is an object (but not an array), check if it can be further collapsed
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      const nested = getCollapsiblePath(value);
      if (nested) {
        return { path: [key, ...nested.path], finalValue: nested.finalValue };
      }
      // Even if we can't collapse further, we can still show this as a collapsed path
      return { path: [key], finalValue: value };
    }

    return null;
  };

  // Preprocess data to flatten single-element arrays and single-child objects
  const preprocessData = (obj: any): any => {
    if (isPrimitive(obj) || obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      // If single-element array, unwrap it
      if (obj.length === 1) {
        return preprocessData(obj[0]);
      }
      // Otherwise, process each element
      return obj.map((item) => preprocessData(item));
    }

    if (typeof obj === "object") {
      const result: any = {};

      for (const [key, value] of Object.entries(obj)) {
        let processedValue = value;

        // First, check if value is a single-element array
        if (Array.isArray(value) && value.length === 1) {
          processedValue = value[0];
        }

        // Then check if it's a single-child object that should be collapsed
        const collapsible = getCollapsiblePath(processedValue);
        if (
          collapsible &&
          collapsible.path.length >= 1 &&
          typeof processedValue === "object" &&
          !Array.isArray(processedValue)
        ) {
          // Flatten the path into a single key
          const flatKey = [key, ...collapsible.path]
            .map((p) => toTitleCase(p))
            .join(" / ");
          result[flatKey] = preprocessData(collapsible.finalValue);
        } else {
          result[toTitleCase(key)] = preprocessData(processedValue);
        }
      }

      return result;
    }

    return obj;
  };

  // Check if an array contains only objects with similar structure (for table display)
  const isArrayOfObjects = (arr: any[]): boolean => {
    if (!Array.isArray(arr) || arr.length === 0) return false;

    // Check if all items are objects (not arrays or primitives)
    if (
      !arr.every(
        (item) =>
          typeof item === "object" && item !== null && !Array.isArray(item)
      )
    ) {
      return false;
    }

    // Get all unique keys from all objects
    const allKeys = new Set<string>();
    arr.forEach((item) => {
      Object.keys(item).forEach((key) => allKeys.add(key));
    });

    // Check if objects have mostly similar keys (at least 50% overlap)
    const commonKeys = Array.from(allKeys).filter(
      (key) => arr.filter((item) => key in item).length >= arr.length * 0.5
    );

    // If we have at least 2 common keys, treat as table-worthy
    return commonKeys.length >= 2;
  };

  // Format primitive values
  const formatValue = (value: any): string | React.ReactNode => {
    if (value === null) return <span className="json-null">(empty)</span>;
    if (value === undefined)
      return <span className="json-undefined">(empty)</span>;
    if (typeof value === "boolean")
      return <span className="json-boolean">{value.toString()}</span>;
    if (typeof value === "number")
      return <span className="json-number">{value}</span>;
    if (typeof value === "string") {
      // Handle empty strings
      if (value === "") return <span className="json-string">(empty)</span>;
      return <span className="json-string">{value}</span>;
    }
    return String(value);
  };

  // Preprocess the data at the root level
  const processedData = depth === 0 ? preprocessData(data) : data;

  // Toggle collapsed state for a node
  const toggleCollapse = (nodePath: string) => {
    const newCollapsed = new Set(collapsedNodes);
    if (newCollapsed.has(nodePath)) {
      newCollapsed.delete(nodePath);
    } else {
      newCollapsed.add(nodePath);
    }
    setCollapsedNodes(newCollapsed);
  };

  // Main render logic (defined as inner function to access processedData)
  const renderData = (
    renderValue: any,
    currentDepth: number = 0,
    currentPath: string = ""
  ): React.ReactElement | null => {
    if (isPrimitive(renderValue)) {
      return <div className="json-value">{formatValue(renderValue)}</div>;
    }

    if (Array.isArray(renderValue)) {
      // Check if this array should be rendered as a table
      if (isArrayOfObjects(renderValue)) {
        return renderTable(renderValue);
      }

      // Render as a clean list
      return (
        <div className="json-array">
          {renderValue.map((item, idx) => {
            const itemPath = `${currentPath}[${idx}]`;
            const isCollapsed = collapsedNodes.has(itemPath);
            const isCollapsible = !isPrimitive(item);

            return (
              <div key={idx} className="json-array-item">
                <div className="json-label-container">
                  {isCollapsible && (
                    <button
                      className="json-collapse-btn"
                      onClick={() => toggleCollapse(itemPath)}
                      aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                      {isCollapsed ? (
                        <ChevronRight size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>
                  )}
                  <p className="json-label text-uppercase mb-0">
                    Item {idx + 1}
                  </p>
                </div>
                {!isCollapsed && (
                  <div className="json-nested-content">
                    {renderData(item, currentDepth + 1, itemPath)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    if (typeof renderValue === "object" && renderValue !== null) {
      const entries = Object.entries(renderValue);

      return (
        <div className={`json-object ${currentDepth > 0 ? "json-nested" : ""}`}>
          {entries.map(([key, value]) => {
            const fieldPath = currentPath ? `${currentPath}.${key}` : key;
            const isCollapsed = collapsedNodes.has(fieldPath);
            const isCollapsible = !isPrimitive(value);

            return (
              <div key={key} className="json-field">
                <div className="json-label-container">
                  {isCollapsible && (
                    <button
                      className="json-collapse-btn"
                      onClick={() => toggleCollapse(fieldPath)}
                      aria-label={isCollapsed ? "Expand" : "Collapse"}
                    >
                      {isCollapsed ? (
                        <ChevronRight size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      )}
                    </button>
                  )}
                  <p className="json-label text-uppercase mb-0">{key}</p>
                </div>
                {isPrimitive(value) ? (
                  <div className="json-value">{formatValue(value)}</div>
                ) : (
                  !isCollapsed && (
                    <div className="json-nested-content">
                      {renderData(value, currentDepth + 1, fieldPath)}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  // Render array as table (defined here to use renderData)
  const renderTable = (arr: any[]): React.ReactElement | null => {
    if (arr.length === 0) return null;

    // Get all unique keys across all objects
    const allKeys = new Set<string>();
    arr.forEach((item) => {
      Object.keys(item).forEach((key) => allKeys.add(key));
    });
    const columns = Array.from(allKeys);

    return (
      <div className="json-table-wrapper">
        <table className="json-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {arr.map((item, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col}>
                    {renderData(item[col], 1, `table[${idx}].${col}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return <>{renderData(processedData)}</>;
};
